/**
 * @file
 * The alert class.
 */

import { isValidClassList, isValidType } from "../validate.js";
import { addClass, removeClass } from "../domHelpers.js";
import { keyPress, preventEvent } from "../eventHandlers.js";
import { TransactionalValue } from "../TransactionalValue.js";
import Component from "../Component.js";

class Alert extends Component {
  /**
   * The DOM elements within the alert.
   *
   * @protected
   *
   * @type {Object<HTMLElement>}
   *
   * @property {HTMLElement} alert      - The alert element.
   * @property {HTMLElement} controller - The alert toggle element.
   */
  _dom = {
    alert: null,
    controller: null,
  };

  /**
   * The DOM elements within the component that cannot be reset or generated by the component itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["alert", "controller"];

  /**
   * The CSS classes to apply when the alert is in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   *
   * @property {string|string[]} open       - The class(es) to apply when the alert is open.
   * @property {string|string[]} close      - The class(es) to apply when the alert is closed.
   * @property {string|string[]} tranistion - The class(es) to apply when the alert is transitioning between states.
   * @property {string|string[]} initialize - The class(es) to apply when the alert is initializing.
   */
  _classes = {
    show: "",
    hide: "",
    transition: "",
    initialize: "",
  };

  /**
   * The duration times (in milliseconds) for variouse aspects throughout the disclosure.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition - The duration time (in milliseconds) for the transition between shown and hidden states.
   * @property {number} show       - The duration time (in milliseconds) for the transition from hidden to shown states.
   * @property {number} hide       - The duration time (in milliseconds) for the transition from shown to hidden states.
   */
  _durations = {
    transition: 150,
    show: -1,
    hide: -1,
  };

  /**
   * The hidden state of the alert.
   *
   * @protected
   *
   * @type {TransactionalValue<boolean>}
   */
  _hidden = new TransactionalValue(false);

  /**
   * Custom events that can be triggered throughout the alert.
   *
   * @protected
   *
   * @type {Object<CustomEvent>}
   */
  _events = {
    /**
     * The event that is triggered when the alert is shown.
     *
     * @event grauplAlertShow
     *
     * @type {CustomEvent}
     *
     * @property {boolean}       bubbles      - A flag to bubble the event
     * @property {Object<Alert>} detail       - The details object containing the alert itself.
     * @property {Alert}         detail.alert - The alert.
     */
    show: new CustomEvent("grauplAlertShow", {
      bubbles: true,
      detail: {
        alert: this,
      },
    }),
    /**
     * The event that is triggered when the alert is hidden.
     *
     * @event grauplAlertHide
     *
     * @type {CustomEvent}
     *
     * @property {boolean}       bubbles      - A flag to bubble the event
     * @property {Object<Alert>} detail       - The details object containing the alert itself.
     * @property {Alert}         detail.alert - The alert.
     */
    hide: new CustomEvent("grauplAlertHide", {
      bubbles: true,
      detail: {
        alert: this,
      },
    }),
  };

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "alerts";

  /**
   * Constructs a new `Alert`.
   *
   * @param {object}               [options = {}]                            - The options object.
   * @param {HTMLElement}          options.alertElement                      - The alert element.
   * @param {?HTMLElement}         [options.controllerElement = null]        - The controller element.
   * @param {string|string[]|null} [options.showClass = show]                - The class(es) to apply when the alert is shown.
   * @param {string|string[]|null} [options.hideClass = hide]                - The class(es) to apply when the alert is hidden.
   * @param {string|string[]|null} [options.transitionClass = transitioning] - The class(es) to apply when the alert is transitioning between states.
   * @param {number}               [options.transitionDuration = 150]        - The duration of the transition between "shown" and "hidden" states (in milliseconds).
   * @param {boolean}              [options.showDuration = -1]               - The duration of the transition from "hidden" to "shown" states (in milliseconds).
   * @param {boolean}              [options.hideDuration = -1]               - The duration of the transition from "shown" to "hidden" states (in milliseconds).
   * @param {boolean}              [options.isHidden = false]                - A flag to determine the initial state of the alert.
   * @param {?string}              [options.prefix = graupl-]                - The prefix used for CSS custom properties and attributes.
   * @param {?string}              [options.key = null]                      - The key used to generate IDs throughout the alert.
   * @param {?(string|string[])}   [options.initializeClass = initializing]  - The class(es) to apply when the alert is initializing.
   * @param {boolean}              [options.initialize = false]              - A flag to initialize the alert immediately upon creation.
   */
  constructor({
    alertElement,
    controllerElement = null,
    showClass = "show",
    hideClass = "hide",
    transitionClass = "transitioning",
    transitionDuration = 150,
    showDuration = -1,
    hideDuration = -1,
    isHidden = false,
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
    initialize = false,
  } = {}) {
    super({
      prefix,
      key,
      initializeClass,
    });

    // Set the DOM elements.
    this._dom.alert = alertElement;
    this._dom.controller = controllerElement;

    // Set the classes.
    this._classes.show = showClass || "";
    this._classes.hide = hideClass || "";
    this._classes.transition = transitionClass || "";

    // Set the durations.
    this._durations.transition = transitionDuration;
    this._durations.show = showDuration;
    this._durations.hide = hideDuration;

    // Set hidden.
    this._hidden.value = isHidden;
    this._hidden.commit();

    if (initialize) {
      this.initialize();
    }
  }

  /**
   * Initializes the alert.
   */
  initialize() {
    try {
      // Add the initialization class.
      addClass(this._classes.initialize, this.dom.alert);

      super.initialize();

      // Set up the DOM.
      this._setIds();
      this._setCustomProps();

      // Handle events.
      this._handleClick();
      this._handleKeydown();
      this._handleKeyup();

      // Store the component.
      this._store();

      // Handle hiding the alert by default.
      if (this.isHidden) {
        this._conseal({ emit: false, transition: false });
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Remove the initialization class.
      requestAnimationFrame(() => {
        removeClass(this._classes.initialize, this.dom.alert);
      });
    }
  }

  /**
   * The class to use the show the alert.
   *
   * @type {string|string[]}
   *
   * @see _classes.show
   */
  get showClass() {
    return this._classes.show;
  }

  set showClass(value) {
    isValidClassList({ showClass: value });

    if (this._classes.show !== value) {
      this._classes.show = value;
    }
  }

  /**
   * The class to use to hide the alert.
   *
   * @type {string|string[]}
   *
   * @see _classes.hide
   */
  get hideClass() {
    return this._classes.hide;
  }

  set hideClass(value) {
    isValidClassList({ hideClass: value });

    if (this._classes.hide !== value) {
      this._classes.hide = value;
    }
  }

  /**
   * The class to use when transitioning the alert.
   *
   * @type {string|string[]}
   *
   * @see _classes.transition
   */
  get transitionClass() {
    return this._classes.transition;
  }

  set transitionClass(value) {
    isValidClassList({ transitionClass: value });

    if (this._classes.transition !== value) {
      this._classes.transition = value;
    }
  }

  /**
   * The time in milliseconds the transition will take.
   *
   * @type {number}
   *
   * @see _durations.transition
   */
  get transitionDuration() {
    return this._durations.transition;
  }

  set transitionDuration(value) {
    isValidType("number", { transitionDuration: value });

    if (this._durations.transition !== value) {
      this._durations.transition = value;
      this._setCustomProps();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from hidden to shown states.
   *
   * If showDuration is set to -1, the transitionDuration value will be used instead.
   *
   * Setting this value will also set the --graupl-show-transition-duration CSS custom property on the disclosure.
   *
   * @type {number}
   *
   * @see _durations.show
   */
  get showDuration() {
    if (this._durations.show === -1) return this.transitionDuration;

    return this._durations.show;
  }

  set showDuration(value) {
    isValidType("number", { value });

    if (this._durations.show !== value) {
      this._durations.show = value;
      this._setCustomProps();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from shown to hidden states.
   *
   * If hideDuration is set to -1, the transitionDuration value will be used instead.
   *
   * Setting this value will also set the --graupl-close-transition-duration CSS custom property on the disclosure.
   *
   * @type {number}
   *
   * @see _durations.hide
   */
  get hideDuration() {
    if (this._durations.hide === -1) return this.transitionDuration;

    return this._durations.hide;
  }

  set hideDuration(value) {
    isValidType("number", { value });

    if (this._durations.hide !== value) {
      this._durations.hide = value;
      this._setCustomProps();
    }
  }

  /**
   * The hidden state of the alert.
   *
   * @readonly
   *
   * @type {boolean}
   *
   * @see _hidden
   */
  get isHidden() {
    return this._hidden.value;
  }

  /**
   * Validates all aspects of the alert to ensure proper functionality.
   *
   * @protected
   *
   * @return {boolean} - The result of the validation.
   */
  _validate() {
    super._validate();

    // Boolean checks.
    const booleans = {
      isHidden: this._hidden.value,
    };

    // Check the booleans.
    const booleanChecks = isValidType("boolean", booleans);

    // Handle boolean check failure.
    if (!booleanChecks) {
      this._errors.push(booleanChecks.message);
      this._valid = false;
    }

    return this._valid;
  }

  /**
   * Sets the IDs of the alert and it's children if they do not already exist.
   *
   * The generated IDs use the key and follow the format:
   *  - alert: `alert-${key}`
   *  - controller: `alert-controller-${key}`
   */
  _setIds() {
    this.dom.alert.id = this.dom.alert.id || `alert-${this.key}`;
    this.dom.controller.id =
      this.dom.controller.id || `alert-controller-${this.key}`;

    this._id = this.dom.alert.id;
  }

  /**
   * Reveals the alert.
   *
   * Adds the show class and removes the hide class from the alert.
   *
   * @protected
   *
   * @fires grauplAlertShow
   *
   * @param {Object<boolean>} [options = {}]              - Options for revealing the alert.
   * @param {boolean}         [options.emit = true]       - Emit the show event once revealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _reveal({ emit = true, transition = true } = {}) {
    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the hide class,
    // add the show class, and finally remove the transition class.
    if (transition && this.transitionClass !== "") {
      addClass(this.transitionClass, this.dom.alert);

      requestAnimationFrame(() => {
        removeClass(this.hideClass, this.dom.alert);

        requestAnimationFrame(() => {
          addClass(this.showClass, this.dom.alert);

          requestAnimationFrame(() => {
            this._setTimeout(
              () => removeClass(this.transitionClass, this.dom.alert),
              this.showDuration
            );
          });
        });
      });
    } else {
      // Add the show class
      addClass(this.showClass, this.dom.alert);

      // Remove the hide class.
      removeClass(this.hideClass, this.dom.alert);
    }

    this.dom.alert.removeAttribute("inert");

    if (emit) {
      this._dispatchEvent("show", this.dom.alert);
    }
  }

  /**
   * Conseals the alert.
   *
   * Adds the hide class and removes the show class from the alert.
   *
   * @protected
   *
   * @fires grauplAlertHide
   *
   * @param {Object<boolean>} [options = {}]              - Options for concealing the alert.
   * @param {boolean}         [options.emit = true]       - Emit the show event once concealed.
   * @param {boolean}         [options.transition = true] - Respect the transition class.
   */
  _conseal({ emit = true, transition = true } = {}) {
    // If we're dealing with transition classes, then we need to utilize
    // requestAnimationFrame to add the transition class, remove the show class,
    // add the hide class, and finally remove the transition class.
    if (transition && this.transitionClass !== "") {
      addClass(this.transitionClass, this.dom.alert);

      requestAnimationFrame(() => {
        removeClass(this.showClass, this.dom.alert);

        requestAnimationFrame(() => {
          addClass(this.hideClass, this.dom.alert);

          requestAnimationFrame(() => {
            this._setTimeout(
              () => removeClass(this.transitionClass, this.dom.alert),
              this.hideDuration
            );
          });
        });
      });
    } else {
      // Add the hide class
      addClass(this.hideClass, this.dom.alert);

      // Remove the show class.
      removeClass(this.showClass, this.dom.alert);
    }

    this.dom.alert.setAttribute("inert", "true");

    if (emit) {
      this._dispatchEvent("hide", this.dom.alert);
    }
  }

  /**
   * Sets custom props throughout the alert.
   *
   * The custom properties are:
   *   - `--graupl-alert-transition-duration`,
   *   - `--graupl-alert-show-transition-duration`, and
   *   - `--graupl-alert-hide-transition-duration`.
   *
   * The prefix of `graupl-` can be changed by setting the alert's prefix value.
   *
   * @protected
   */
  _setCustomProps() {
    this.dom.alert.style.setProperty(
      `--${this.prefix}alert-transition-duration`,
      `${this.transitionDuration}ms`
    );

    this.dom.alert.style.setProperty(
      `--${this.prefix}alert-show-transition-duration`,
      `${this.showDuration}ms`
    );

    this.dom.alert.style.setProperty(
      `--${this.prefix}alert-hide-transition-duration`,
      `${this.hideDuration}ms`
    );
  }

  /**
   * Handles click events throughout the alert for proper use.
   *
   * - Adds a `pointerup` listener to the controller that will hide the alert.
   *
   * @protected
   */
  _handleClick() {
    if (this.dom.controller === null) {
      return;
    }

    this._addEventListener("pointerup", this.dom.controller, () => this.hide());
  }

  /**
   * Handles keydown events throughout the alert for proper use.
   *
   * This method exists to assist the _handleKeyup method.
   *
   * - Adds a `keydown` listener to the controller (if it exists).
   *   - Blocks propagation on "Space" and "Enter" keys.
   */
  _handleKeydown() {
    if (this.dom.controller === null) {
      return;
    }

    this._addEventListener("keydown", this.dom.controller, (event) => {
      const key = keyPress(event);

      // Prevent default behavior for space and enter keys.
      if (key === "Space" || key === "Enter") {
        preventEvent(event);
      }
    });
  }

  /**
   * Handles keyup events throughout the alert for proper use.
   *
   * - Adds a `keyup` listener to the controller (if it exists).
   *   - Hides the alert when the user hits "Space" or "Enter".
   */
  _handleKeyup() {
    if (this.dom.controller === null) {
      return;
    }

    this._addEventListener("keyup", this.dom.controller, (event) => {
      const key = keyPress(event);

      if (key === "Space" || key === "Enter") {
        this.hide();
      }
    });
  }

  /**
   * Shows the alert.
   *
   * Sets the alert's focus state to "self", calls reveal, and sets isHidden to `false`.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for showing the alert.
   * @param {boolean}         [options.force = false]         - Whether to force the show action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the hidden state.
   */
  show({ force = false, preserveState = false } = {}) {
    if (!this.isHidden && !force) return;

    // Set the focus state.
    this.focusState = "self";

    // Reveal the alert.
    this._reveal();

    // Set the hidden state.
    this._hidden.value = false;

    if (!preserveState) {
      this._hidden.commit();
    }
  }

  /**
   * Hides the alert.
   *
   * Sets the alert's focus state to "none", calls conceal, and sets isHidden to `true`.
   *
   * @param {Object<boolean>} [options = {}]                  - Options for hiding the alert.
   * @param {boolean}         [options.force = false]         - Whether to force the hide action.
   * @param {boolean}         [options.preserveState = false] - Whether to preserve the hidden state.
   */
  hide({ force = false, preserveState = false } = {}) {
    if (this.isHidden && !force) return;

    // Set the focus state.
    this.focusState = "none";

    // Conceal the alert.
    this._conseal();

    // Set the hidden state.
    this._hidden.value = true;

    if (!preserveState) {
      this._hidden.commit();
    }
  }
}

export default Alert;
